import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box, Button, TextField, Chip, AppBar, Toolbar, Menu, MenuItem, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VotingSystem from '../components/VotingSystem';
import { doc, getDoc, setDoc, addDoc, collection, deleteDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { firebaseConfig } from '../firebaseConfig';
import { initializeApp } from 'firebase/app';
import DriveFileMoveRtlIcon from '@mui/icons-material/DriveFileMoveRtl';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);

// Function to read plan data from Firebase
const readPlanDataFromFirebase = async (planId: string) => {
  try {
    const docRef = doc(db, 'marketingPlan', planId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      console.log(`No such document: ${planId}`);
      return null;
    }
  } catch (error) {
    console.error(`Error reading plan data from Firebase:`, error);
    return null;
  }
};

// Function to save data to Firebase
const saveDataToFirebase = async (planId: string, data: any) => {
  try {
    const docRef = doc(db, 'marketingPlan', planId);
    await setDoc(docRef, { ...data, updatedAt: new Date() }, { merge: true });
    console.log(`Data saved to plan ${planId} successfully`);
  } catch (error) {
    console.error(`Error saving to plan ${planId}:`, error);
  }
};

// Function to move a marketing plan back to bucket list
const moveToBucketList = async (planData: any, planId: string) => {
  try {
    // Create the bucket document
    const bucketData = {
      name: planData.name,
      description: planData.description,
      expectedBudget: planData.expectedBudget || 0,
      status: planData.status || 'open',
      expectedScope: planData.expectedScope || 'mid',
      expectedReturn: planData.expectedReturn || 'mid',
      files: planData.files || [],
      urls: planData.urls || [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add to bucket list collection
    const bucketRef = await addDoc(collection(db, 'marketingBucketList'), bucketData);

    // Delete from marketing plan collection
    const planRef = doc(db, 'marketingPlan', planId);
    await deleteDoc(planRef);

    console.log(`Marketing plan ${planId} moved to bucket list ${bucketRef.id} successfully`);
    return bucketRef.id;
  } catch (error) {
    console.error('Error moving plan to bucket list:', error);
    return null;
  }
};

// Editable Text Component
const EditableText: React.FC<{
  value: string;
  onSave: (value: string) => void;
  variant?: 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2';
  sx?: any;
  placeholder?: string;
  multiline?: boolean;
}> = ({ value, onSave, variant = 'body1', sx = {}, placeholder = '', multiline = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        variant="outlined"
        placeholder={placeholder}
        fullWidth
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        maxRows={multiline ? 6 : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: variant === 'h3' ? '1.5rem' : variant === 'h4' ? '1.5rem' : variant === 'h5' ? '1.25rem' : variant === 'h6' ? '1.25rem' : '1rem',
            fontWeight: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? 'bold' : 'normal',
            minHeight: 'auto',
            padding: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? '8px 12px' : '4px 8px',
          },
          '& .MuiInputBase-input': {
            padding: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? '8px 12px' : '4px 8px',
          },
          ...sx,
        }}
      />
    );
  }

  return (
    <Box
      component="span"
      onClick={() => setIsEditing(true)}
      sx={{
        typography: variant,
        cursor: 'pointer',
        display: 'inline-block',
        minWidth: variant === 'h3' ? '80px' : '60px',
        minHeight: '1.2em',
        border: '1px dashed transparent',
        borderRadius: 1,
        padding: '2px 4px',
        transition: (theme) => theme.transitions.create(['background-color', 'border-color']),
        '&:hover': {
          borderColor: 'divider',
          backgroundColor: 'action.hover',
        },
        ...sx,
      }}
    >
      {value || placeholder}
    </Box>
  );
};

// Editable Number Component
const EditableNumber: React.FC<{
  value: number;
  onSave: (value: number) => void;
  formatCurrency?: boolean;
  variant?: 'h3' | 'h6' | 'body1' | 'body2';
  sx?: any;
}> = ({ value, onSave, formatCurrency = false, variant = 'body1', sx = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());

  useEffect(() => {
    setEditValue(value.toString());
  }, [value]);

  const handleSave = () => {
    const newValue = parseFloat(editValue);
    if (!isNaN(newValue) && newValue !== value) {
      onSave(newValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value.toString());
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <TextField
        type="number"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        size="small"
        sx={{
          width: '120px',
          '& .MuiOutlinedInput-root': {
            fontSize: variant === 'h6' ? '1.25rem' : '1rem',
          },
          ...sx
        }}
      />
    );
  }

  return (
    <Box
      component="span"
      onClick={() => setIsEditing(true)}
      sx={{
        typography: variant,
        cursor: 'pointer',
        minWidth: '50px',
        display: 'inline-block',
        ...sx
      }}
    >
      {formatCurrency ? `$${value.toLocaleString()}` : value.toLocaleString()}
    </Box>
  );
};

// EditableChip Component for Status/Scope/Return
const EditableChip: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onSave: (value: string) => void;
  getColor: (value: string) => "default" | "error" | "warning" | "info" | "success" | "primary" | "secondary";
}> = ({ value, options, onSave, getColor }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newValue: string) => {
    onSave(newValue);
    handleClose();
  };

  const currentOption = options.find(opt => opt.value === value);

  return (
    <>
      <Chip
        label={currentOption ? currentOption.label : value}
        onClick={handleClick}
        color={getColor(value)}
        size="small"
        sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => handleSelect(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// File handling functions
const handleFileUpload = async (files: File[], planId: string, planData: any, setPlanData: any, savePlanData: any) => {
  const uploadedFiles = [];
  
  for (const file of files) {
    try {
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `marketing/${planId}/${fileName}`);
      
      // Upload file to Firebase Storage
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Add file info to the list
      uploadedFiles.push({
        name: file.name,
        size: file.size,
        url: downloadURL,
        uploadedAt: new Date(),
        storagePath: `marketing/${planId}/${fileName}`
      });
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  }
  
  // Update plan data with new files
  const updatedFiles = [...(planData.files || []), ...uploadedFiles];
  const updatedData = { ...planData, files: updatedFiles };
  setPlanData(updatedData);
  await savePlanData('files', updatedFiles);
};

const handleFileDelete = async (index: number, planId: string, planData: any, setPlanData: any, savePlanData: any) => {
  try {
    const fileToDelete = planData.files[index];
    
    // Delete from Firebase Storage
    if (fileToDelete.storagePath) {
      const storageRef = ref(storage, fileToDelete.storagePath);
      await deleteObject(storageRef);
    }
    
    // Remove from plan data
    const updatedFiles = planData.files.filter((_: any, i: number) => i !== index);
    const updatedData = { ...planData, files: updatedFiles };
    setPlanData(updatedData);
    await savePlanData('files', updatedFiles);
  } catch (error) {
    console.error('Error deleting file:', error);
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// URL handling functions
const handleAddUrl = async (title: string, url: string, planId: string, planData: any, setPlanData: any, savePlanData: any) => {
  if (!url.trim()) return;
  
  const newUrl = {
    title: title.trim() || 'Untitled',
    url: url.trim(),
    addedAt: new Date()
  };
  
  const updatedUrls = [...(planData.urls || []), newUrl];
  const updatedData = { ...planData, urls: updatedUrls };
  setPlanData(updatedData);
  await savePlanData('urls', updatedUrls);
};

const handleDeleteUrl = async (index: number, planId: string, planData: any, setPlanData: any, savePlanData: any) => {
  const updatedUrls = planData.urls.filter((_: any, i: number) => i !== index);
  const updatedData = { ...planData, urls: updatedUrls };
  setPlanData(updatedData);
  await savePlanData('urls', updatedUrls);
};

interface MarketingPlanProps {
  planId: string;
  planName: string;
  onBackToPlans: () => void;
}

const MarketingPlan: React.FC<MarketingPlanProps> = ({ planId, planName, onBackToPlans }) => {
  const [planData, setPlanData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [newUrlTitle, setNewUrlTitle] = useState('');
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  // Load plan data from Firebase
  const loadPlanData = async () => {
    try {
      const data = await readPlanDataFromFirebase(planId);
      setPlanData(data);
    } catch (error) {
      console.error('Error loading plan data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save plan data to Firebase
  const savePlanData = async (field: string, value: any) => {
    if (!planData) return;
    
    const updatedData = { ...planData, [field]: value };
    setPlanData(updatedData);
    await saveDataToFirebase(planId, updatedData);
  };

  // Load data on component mount
  useEffect(() => {
    loadPlanData();
  }, [planId]);

  const handleMoveToBucketList = async () => {
    if (!planData) return;
    
    setLoading(true);
    const bucketId = await moveToBucketList(planData, planId);
    if (bucketId) {
      setMoveDialogOpen(false);
      onBackToPlans(); // Go back to plans list after successful move
    }
    setLoading(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in progress': return 'warning';
      case 'open': return 'default';
      default: return 'default';
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'high': return 'error';
      case 'mid': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getReturnColor = (returnValue: string) => {
    switch (returnValue) {
      case 'high': return 'success';
      case 'mid': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-100 text-gray-800 min-h-screen flex items-center justify-center">
        <Typography variant="h6">Loading plan data...</Typography>
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="bg-gray-100 text-gray-800 min-h-screen flex items-center justify-center">
        <Typography variant="h6">Plan not found</Typography>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 text-gray-800 min-h-screen">
      <AppBar position="sticky" sx={{ top: 16, borderRadius: 2, mb: 4, zIndex: 50 }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button 
              variant="text" 
              onClick={onBackToPlans}
              startIcon={<ArrowBackIcon />}
              sx={{ color: 'white' }}
            >
              Back to Plans
            </Button>
            <Typography variant="h6" sx={{ color: 'white', ml: 2 }}>
              {planName}
            </Typography>
            <VotingSystem 
              collectionName="marketingPlan" 
              documentId={planId} 
              size="small"
              showCounts={true}
              showTooltips={true}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<DriveFileMoveRtlIcon />}
              onClick={() => setMoveDialogOpen(true)}
              sx={{ color: 'white' }}
            >
              Move to Bucket List
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8">
        <Grid container spacing={4}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Basic Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Plan Name
                    </Typography>
                    <EditableText
                      value={planData?.name || ''}
                      onSave={(value) => savePlanData('name', value)}
                      variant="h6"
                      sx={{ fontWeight: 'medium' }}
                      placeholder="Enter plan name"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <EditableChip
                      value={planData?.status || 'open'}
                      options={[
                        { value: 'open', label: 'Open' },
                        { value: 'in progress', label: 'In Progress' },
                        { value: 'completed', label: 'Completed' }
                      ]}
                      onSave={(value) => savePlanData('status', value)}
                      getColor={getStatusColor}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Owner
                    </Typography>
                    <EditableText
                      value={planData?.owner || ''}
                      onSave={(value) => savePlanData('owner', value)}
                      variant="h6"
                      sx={{ fontWeight: 'medium' }}
                      placeholder="Enter owner name"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Agency
                    </Typography>
                    <EditableText
                      value={planData?.agency || ''}
                      onSave={(value) => savePlanData('agency', value)}
                      variant="h6"
                      sx={{ fontWeight: 'medium' }}
                      placeholder="Enter agency name"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <EditableText
                      value={planData?.description || ''}
                      onSave={(value) => savePlanData('description', value)}
                      variant="body1"
                      placeholder="Enter description"
                      multiline={true}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Budget and Expectations */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Budget & Expectations
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Expected Budget
                    </Typography>
                    <EditableNumber
                      value={planData?.expectedBudget || 0}
                      onSave={(value) => savePlanData('expectedBudget', value)}
                      formatCurrency={true}
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: 'primary.main' }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Expected Scope
                    </Typography>
                    <EditableChip
                      value={planData?.expectedScope || 'mid'}
                      options={[
                        { value: 'low', label: 'Low' },
                        { value: 'mid', label: 'Mid' },
                        { value: 'high', label: 'High' }
                      ]}
                      onSave={(value) => savePlanData('expectedScope', value)}
                      getColor={getScopeColor}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Expected Return
                    </Typography>
                    <EditableChip
                      value={planData?.expectedReturn || 'mid'}
                      options={[
                        { value: 'low', label: 'Low' },
                        { value: 'mid', label: 'Mid' },
                        { value: 'high', label: 'High' }
                      ]}
                      onSave={(value) => savePlanData('expectedReturn', value)}
                      getColor={getReturnColor}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* URL Links */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  URL Links
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add relevant URLs and links for this marketing plan.
                </Typography>
                
                {/* URL Input */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                      placeholder="Title (e.g., Company Website)"
                      variant="outlined"
                      size="small"
                      value={newUrlTitle}
                      onChange={(e) => setNewUrlTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (newUrl.trim()) {
                            handleAddUrl(newUrlTitle, newUrl, planId, planData, setPlanData, savePlanData);
                            setNewUrl('');
                            setNewUrlTitle('');
                          }
                        }
                      }}
                      sx={{ minWidth: '200px', flexGrow: 1 }}
                    />
                    <TextField
                      placeholder="Enter URL (e.g., https://example.com)"
                      variant="outlined"
                      size="small"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (newUrl.trim()) {
                            handleAddUrl(newUrlTitle, newUrl, planId, planData, setPlanData, savePlanData);
                            setNewUrl('');
                            setNewUrlTitle('');
                          }
                        }
                      }}
                      sx={{ minWidth: '300px', flexGrow: 2 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        if (newUrl.trim()) {
                          handleAddUrl(newUrlTitle, newUrl, planId, planData, setPlanData, savePlanData);
                          setNewUrl('');
                          setNewUrlTitle('');
                        }
                      }}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>

                {/* URL List */}
                {planData?.urls && planData.urls.length > 0 && (
                  <List>
                    {planData.urls.map((urlItem: any, index: number) => (
                      <ListItem key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                        <ListItemText
                          primary={
                            <a 
                              href={urlItem.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#1976d2', textDecoration: 'none' }}
                            >
                              {urlItem.title}
                            </a>
                          }
                          secondary={`${urlItem.url} • Added: ${new Date(urlItem.addedAt).toLocaleDateString()}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUrl(index, planId, planData, setPlanData, savePlanData)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Files */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Files
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload PDFs and other relevant files for this marketing plan.
                </Typography>
                
                {/* File Upload */}
                <Box sx={{ mb: 3 }}>
                  <input
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                    style={{ display: 'none' }}
                    id="file-upload-plan"
                    multiple
                    type="file"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files) {
                        handleFileUpload(Array.from(files), planId, planData, setPlanData, savePlanData);
                      }
                    }}
                  />
                  <label htmlFor="file-upload-plan">
                    <Button variant="outlined" component="span">
                      Upload Files
                    </Button>
                  </label>
                </Box>

                {/* File List */}
                {planData?.files && planData.files.length > 0 && (
                  <List>
                    {planData.files.map((file: any, index: number) => (
                      <ListItem key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                        <ListItemText
                          primary={file.name}
                          secondary={`Size: ${formatFileSize(file.size)} • Uploaded: ${new Date(file.uploadedAt).toLocaleDateString()}`}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open(file.url, '_blank')}
                            >
                              View
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDelete(index, planId, planData, setPlanData, savePlanData)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Metadata */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Metadata
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Created At
                    </Typography>
                    <Typography variant="body2">
                      {planData?.createdAt?.toDate?.()?.toLocaleString() || 'Unknown'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {planData?.updatedAt?.toDate?.()?.toLocaleString() || 'Unknown'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>

      {/* Move to Bucket List Confirmation Dialog */}
      <Dialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        aria-labelledby="move-dialog-title"
      >
        <DialogTitle id="move-dialog-title">
          Move to Bucket List
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to move "{planData?.name}" from the marketing plan to the bucket list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setMoveDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMoveToBucketList}
            color="warning"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Moving...' : 'Move to Bucket List'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MarketingPlan; 